import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Interface for custom breadcrumb labels
 * Maps URL segments to custom display labels
 */
export interface BreadcrumbOverride {
  segment: string;  // The URL segment (e.g., "693966b2fce718b4c338afa6")
  label: string;    // The custom label to display (e.g., "TELE9255")
}

/**
 * BreadcrumbService
 * 
 * Allows components to set custom breadcrumb labels for specific URL segments.
 * This is useful for displaying user-friendly IDs (like "TELE9255") instead of
 * database IDs (like "693966b2fce718b4c338afa6") in breadcrumbs.
 * 
 * Usage:
 * ```typescript
 * // In a component's ngOnInit:
 * this.breadcrumbService.setBreadcrumbOverride('693966b2fce718b4c338afa6', 'TELE9255');
 * 
 * // In a component's ngOnDestroy:
 * this.breadcrumbService.clearBreadcrumbOverride('693966b2fce718b4c338afa6');
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private overridesSubject = new BehaviorSubject<Map<string, string>>(new Map());
  public overrides$: Observable<Map<string, string>> = this.overridesSubject.asObservable();

  constructor() {}

  /**
   * Set a custom breadcrumb label for a specific URL segment
   * @param segment - The URL segment (e.g., database ID)
   * @param label - The custom label to display (e.g., display ID)
   */
  setBreadcrumbOverride(segment: string, label: string): void {
    const currentOverrides = this.overridesSubject.value;
    currentOverrides.set(segment, label);
    this.overridesSubject.next(new Map(currentOverrides));
  }

  /**
   * Clear a custom breadcrumb label for a specific URL segment
   * @param segment - The URL segment to clear
   */
  clearBreadcrumbOverride(segment: string): void {
    const currentOverrides = this.overridesSubject.value;
    currentOverrides.delete(segment);
    this.overridesSubject.next(new Map(currentOverrides));
  }

  /**
   * Clear all custom breadcrumb labels
   */
  clearAllOverrides(): void {
    this.overridesSubject.next(new Map());
  }

  /**
   * Get the custom label for a URL segment, if one exists
   * @param segment - The URL segment
   * @returns The custom label, or null if no override exists
   */
  getOverrideLabel(segment: string): string | null {
    return this.overridesSubject.value.get(segment) || null;
  }

  /**
   * Get all current overrides
   * @returns Map of segment -> label overrides
   */
  getAllOverrides(): Map<string, string> {
    return new Map(this.overridesSubject.value);
  }
}

